import { SharedLinkResponse } from '@/modules/Drive/types'
import { PermissionLevel } from '@/modules/Drive/utils/DrivePermission/permission'
import { copyToClipboard } from '@/utils/function'
import { t } from 'i18next'
import { useState } from 'react'
import { Button, Dialog } from 'vnetwork-ui'
import LinkIcons from '@/icons/LinkIcons'

import { Sections } from '@/stores/useDriveStore'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'

interface PublicLinksSectionProps {
    publicLinks: SharedLinkResponse[]
    onRevokeLink?: (shareLinkId: number) => Promise<void>
    isLoading?: boolean
}

const PublicLinksSection = ({
    publicLinks,
    onRevokeLink,
    isLoading = false,
}: PublicLinksSectionProps) => {
    const [confirmRevoke, setConfirmRevoke] = useState<{
        open: boolean
        shareLinkId: number | null
    }>({ open: false, shareLinkId: null })
    const [revoking, setRevoking] = useState(false)

    const getPermissionLabel = (level: PermissionLevel) => {
        switch (level) {
            case PermissionLevel.EDIT:
                return t('can-edit')
            case PermissionLevel.READ:
                return t('can-view')
            case PermissionLevel.VIEW:
                return t('can-view-and-not-download')
            default:
                return ''
        }
    }

    const getPermissionColor = (level: PermissionLevel) => {
        switch (level) {
            case PermissionLevel.EDIT:
                return 'bg-blue-100 text-blue-700'
            case PermissionLevel.READ:
                return 'bg-green-100 text-green-700'
            case PermissionLevel.VIEW:
                return 'bg-yellow-100 text-yellow-700'
            default:
                return 'bg-gray-100 text-gray-700'
        }
    }

    const generateShareUrl = (shareLinkToken: string) => {
        return `${window.location.origin}/drive/${Sections.Shared}?token=${shareLinkToken}`
    }

    const handleCopyLink = (shareLinkToken: string) => {
        const url = generateShareUrl(shareLinkToken)
        copyToClipboard(url, t)
    }

    const handleRevokeClick = (shareLinkId: number) => {
        setConfirmRevoke({ open: true, shareLinkId: shareLinkId })
    }

    const handleConfirmRevoke = async () => {
        if (confirmRevoke.shareLinkId) {
            setRevoking(true)
            try {
                await onRevokeLink?.(confirmRevoke.shareLinkId)
            } catch (error) {
                console.error(error)
            } finally {
                setRevoking(false)
                setConfirmRevoke({
                    open: false,
                    shareLinkId: null,
                })
            }
        }
    }

    if (publicLinks.length === 0) {
        return (
            <div
                className={`w-full mt-2 py-3 flex flex-col gap-1 bg-gray-50 items-center`}
            >
                <p className="text-sm text-center text-gray-400">
                    {t('drive-no-public-links')}
                </p>
            </div>
        )
    }

    return (
        <>
            <div className="flex flex-col gap-3">
                <div className="overflow-y-auto max-h-[50vh]">
                    {publicLinks.map((link) => (
                        <div
                            key={link.share_link_id}
                            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors mb-3"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <LinkIcons className="h-5 w-5 text-gray-500" />
                                        <span className="font-medium text-gray-900">
                                            {t('drive-public-share-link')}
                                        </span>
                                        <span
                                            className={`text-xs px-2 py-1 rounded-full ${getPermissionColor(
                                                link.share_permission_level
                                            )}`}
                                        >
                                            {getPermissionLabel(
                                                link.share_permission_level
                                            )}
                                        </span>
                                    </div>
                                    <div className="bg-gray-100 p-2 rounded text-xs text-gray-600 font-mono break-all">
                                        {generateShareUrl(
                                            link.share_link_token
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={() =>
                                            handleCopyLink(
                                                link.share_link_token
                                            )
                                        }
                                        startIcon={
                                            <LinkIcons className="h-4 w-4" />
                                        }
                                    >
                                        {t('copy-link')}
                                    </Button>
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={() =>
                                            handleRevokeClick(
                                                link.share_link_id
                                            )
                                        }
                                        startIcon={
                                            <DeleteOutlineIcons className="h-4 w-4" />
                                        }
                                        className="text-red-600 hover:text-red-700"
                                        disabled={isLoading}
                                    >
                                        {t('drive-revoke-link')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <Dialog
                open={confirmRevoke.open}
                onClose={() =>
                    setConfirmRevoke({
                        open: false,
                        shareLinkId: null,
                    })
                }
                title={t('confirm-revoke-title')}
                actions={
                    <>
                        <Button
                            variant="outlined"
                            onClick={() =>
                                setConfirmRevoke({
                                    open: false,
                                    shareLinkId: null,
                                })
                            }
                            disabled={revoking}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleConfirmRevoke}
                            loading={revoking}
                            disabled={revoking}
                        >
                            {t('drive-revoke-link')}
                        </Button>
                    </>
                }
            >
                <p className="text-gray-700">{t('confirm-revoke-message')}</p>
                <p className="text-sm text-gray-500 mt-2">
                    {t('revoke-warning')}
                </p>
            </Dialog>
        </>
    )
}

export default PublicLinksSection
